import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search } from "lucide-react";
import ShopifyBuyButton from "@/components/ShopifyBuyButton";
import GoldSingleCard from "@/components/shop/GoldSingleCard";
import GoldSingleRow from "@/components/shop/GoldSingleRow";
import {
  fetchGoldSinglesPremium,
  fetchAllGoldSinglesLight,
} from "@/lib/shopifyStorefront";

/**
 * /shop/gold-singles — Master Gold Crumple Singles page.
 *
 * Two display modes, one page:
 *   • PREMIUM $20+   → image-driven visual cards (28 products)
 *   • $10–$19        → compact list rows           (72 products)
 *   • $9 & Under     → compact list rows           (880 products, paginated)
 *
 * Data strategy — how search covers all 980:
 *   On mount we kick off THREE Shopify Storefront fetches in parallel:
 *     1) fetchGoldSinglesPremium()      → heavy payload (with images), 28
 *     2) fetchAllGoldSinglesLight("midtier")   → light payload, 72 in 1 page
 *     3) fetchAllGoldSinglesLight("underten")  → light payload, 880 in ~4
 *        cursor-paginated batches of 250
 *   All three arrays are held in state. Search / sort / filter operate on
 *   the FULL in-memory catalog — pagination (below) is a rendering
 *   concern only. This means the user can type "Messi" or "ARG18" or
 *   "FWC12" at any time and the correct row/card surfaces even if it
 *   hasn't been rendered yet.
 *
 * Rendering pagination:
 *   Premium bucket   → always renders every matching card
 *   $10–$19 bucket   → always renders every matching row (72 max)
 *   $9 & Under       → initial 36 rows, LOAD MORE reveals +36 each click
 *                       (Search disables the pagination cap.)
 */

const SORT_OPTIONS = [
  { value: "country-asc",  label: "A → Z" },
  { value: "country-desc", label: "Z → A" },
  { value: "price-asc",    label: "PRICE: LOW → HIGH" },
  { value: "price-desc",   label: "PRICE: HIGH → LOW" },
];
const TYPE_OPTIONS = [
  { value: "all",        label: "ALL" },
  { value: "regular",    label: "REGULAR" },
  { value: "team-badge", label: "TEAM BADGE" },
  { value: "fwc",        label: "FWC" },
];

const PAGE_SIZE = 20;   // shared visual pagination step for mid + value tiers

/**
 * Country-name aliases for search only. Panini tags products with the
 * canonical 3-letter FIFA code (e.g. `country-bra`) — the customer-facing
 * country name (e.g. "Brazil") is NOT in the Shopify title of a player
 * card (BRA14 Vinícius Júnior does not contain the literal word
 * "Brazil"). So a search for "Brazil" would otherwise miss every BRA
 * player card. This map lets us match country_code by full/partial
 * country name entirely on the frontend — Shopify products are unchanged.
 *
 * Includes every WC 2026 qualifier plus common historical FIFA codes so
 * future imports don't silently fail search.
 */
const COUNTRY_NAMES = {
  ARG: ["argentina"], AUS: ["australia"], AUT: ["austria"],  BEL: ["belgium"],
  BRA: ["brazil"],    CAN: ["canada"],    CHI: ["chile"],    COL: ["colombia"],
  CRC: ["costa rica"], CRO: ["croatia"],  DEN: ["denmark"],  ECU: ["ecuador"],
  EGY: ["egypt"],     ENG: ["england"],   ESP: ["spain"],    FRA: ["france"],
  GER: ["germany"],   GHA: ["ghana"],     GRE: ["greece"],   HON: ["honduras"],
  IRN: ["iran"],      IRL: ["ireland"],   ISL: ["iceland"],  ITA: ["italy"],
  JAM: ["jamaica"],   JPN: ["japan"],     KOR: ["south korea", "korea"],
  KSA: ["saudi arabia", "saudi"],         MAR: ["morocco"],  MEX: ["mexico"],
  NED: ["netherlands", "holland"],        NGA: ["nigeria"],  NOR: ["norway"],
  NZL: ["new zealand"],                   PAN: ["panama"],   PAR: ["paraguay"],
  PER: ["peru"],      POL: ["poland"],    POR: ["portugal"], QAT: ["qatar"],
  RSA: ["south africa"],                  SCO: ["scotland"], SEN: ["senegal"],
  SLV: ["el salvador"],                   SRB: ["serbia"],   SUI: ["switzerland"],
  SVK: ["slovakia"],  SWE: ["sweden"],    TUN: ["tunisia"],  TUR: ["turkey", "türkiye"],
  UAE: ["united arab emirates", "uae"],   UKR: ["ukraine"],
  URU: ["uruguay"],   USA: ["united states", "usa", "america"],
  UZB: ["uzbekistan"], VEN: ["venezuela"], WAL: ["wales"],
  // FWC / non-country codes intentionally omitted — those products
  // surface via sticker code or title text (e.g. "FWC12", "Brazil 1962").
};

/**
 * Client-side filter used by every bucket. Case-insensitive, partial
 * matching, hits sticker code / player name / subject / country code /
 * country FULL NAME (via COUNTRY_NAMES alias map) / product title /
 * tag list. Empty query returns unfiltered.
 */
function filterProducts(products, query, type) {
  const q = query.trim().toLowerCase();
  return products.filter((p) => {
    if (type !== "all" && p.sticker_type !== type) return false;
    if (!q) return true;
    const aliases = (p.country_code && COUNTRY_NAMES[p.country_code]) || [];
    return (
      String(p.sticker_code   || "").toLowerCase().includes(q) ||
      String(p.subject_name   || "").toLowerCase().includes(q) ||
      String(p.player_name    || "").toLowerCase().includes(q) ||
      String(p.country_code   || "").toLowerCase().includes(q) ||
      aliases.some(name => name.includes(q)) ||
      String(p.product_title  || "").toLowerCase().includes(q) ||
      (Array.isArray(p.tags) && p.tags.some(t => String(t).toLowerCase().includes(q)))
    );
  });
}

/**
 * Sort products by the chosen sort mode. When there is an active search,
 * products whose sticker_code EXACTLY equals the query (case-insensitive)
 * are boosted to the top of their bucket so `URU2` surfaces "URU2 Sergio
 * Rochet" before the partial-match "URU20 Facundo Pellistri". Partial
 * matching remains intact — the boost is purely a sort tie-breaker.
 */
function sortProducts(products, sort, query) {
  const q = String(query || "").trim().toLowerCase();
  const arr = products.slice();
  arr.sort((a, b) => {
    if (q) {
      const aExact = String(a.sticker_code || "").toLowerCase() === q ? 1 : 0;
      const bExact = String(b.sticker_code || "").toLowerCase() === q ? 1 : 0;
      if (aExact !== bExact) return bExact - aExact;
    }
    switch (sort) {
      case "price-desc":   return (b._price_amount || 0) - (a._price_amount || 0);
      case "price-asc":    return (a._price_amount || 0) - (b._price_amount || 0);
      case "country-asc":  return String(a.country_code || "zzz").localeCompare(String(b.country_code || "zzz"));
      case "country-desc": return String(b.country_code || "zzz").localeCompare(String(a.country_code || "zzz"));
      default: return 0;
    }
  });
  return arr;
}

export default function GoldSinglesCatalog() {
  const [query,   setQuery]   = useState("");
  const [sort,    setSort]    = useState("price-desc");
  const [type,    setType]    = useState("all");
  // Mobile-only collapsible filters panel (Sort + Sticker Type). Keeps
  // the sticky header a single compact row when closed — critical
  // for the 390-px viewport where a 3-row filter block eats ~30% of
  // the visible page.
  const [filtersOpen, setFiltersOpen] = useState(false);

  const [premium,  setPremium]  = useState([]);
  const [midtier,  setMidtier]  = useState([]);
  const [underten, setUnderten] = useState([]);
  const [visibleMid,   setVisibleMid]   = useState(PAGE_SIZE);
  const [visibleUnder, setVisibleUnder] = useState(PAGE_SIZE);

  const [loading, setLoading] = useState({ premium: true, midtier: true, underten: true });
  const [errors,  setErrors]  = useState({ premium: null,  midtier: null,  underten: null });

  // Fetch everything on mount, in parallel.
  useEffect(() => {
    let cancelled = false;

    (async () => {
      const p = await fetchGoldSinglesPremium(28);
      if (cancelled) return;
      setPremium(p.products || []);
      setErrors(prev => ({ ...prev, premium: p.meta && p.meta.source === "error" ? p.meta.error : null }));
      setLoading(prev => ({ ...prev, premium: false }));
    })();

    (async () => {
      const m = await fetchAllGoldSinglesLight("midtier", 250);
      if (cancelled) return;
      setMidtier(m.products || []);
      setErrors(prev => ({ ...prev, midtier: m.meta && m.meta.source === "error" ? m.meta.error : null }));
      setLoading(prev => ({ ...prev, midtier: false }));
    })();

    (async () => {
      const u = await fetchAllGoldSinglesLight("underten", 250);
      if (cancelled) return;
      setUnderten(u.products || []);
      setErrors(prev => ({ ...prev, underten: u.meta && u.meta.source === "error" ? u.meta.error : null }));
      setLoading(prev => ({ ...prev, underten: false }));
    })();

    return () => { cancelled = true; };
  }, []);

  const searching = query.trim().length > 0;

  const viewPremium  = useMemo(() => sortProducts(filterProducts(premium,  query, type), sort, query), [premium,  query, type, sort]);
  const viewMidtier  = useMemo(() => sortProducts(filterProducts(midtier,  query, type), sort, query), [midtier,  query, type, sort]);
  const viewUnderten = useMemo(() => sortProducts(filterProducts(underten, query, type), sort, query), [underten, query, type, sort]);

  // Reset $10–$19 + $9 & Under pagination whenever a filter/search changes.
  useEffect(() => {
    setVisibleMid(PAGE_SIZE);
    setVisibleUnder(PAGE_SIZE);
  }, [query, sort, type]);

  // Show all matches when searching (pagination is only for the default
  // browsing view — search must reach every product in the full list).
  const midRendered   = searching ? viewMidtier  : viewMidtier.slice(0, visibleMid);
  const underRendered = searching ? viewUnderten : viewUnderten.slice(0, visibleUnder);

  const totalCatalog = premium.length + midtier.length + underten.length;
  const totalMatches = viewPremium.length + viewMidtier.length + viewUnderten.length;
  const anyLoading   = loading.premium || loading.midtier || loading.underten;

  return (
    <div className="min-h-screen bg-white" data-testid="gold-singles-page">
      {/* Hero */}
      <section className="bg-[var(--ink)] text-white py-12 md:py-16">
        <div className="container-gm">
          <span className="sticker sticker-yellow">GOLD CRUMPLE</span>
          <h1 className="h-display mt-4 text-4xl md:text-6xl"
              data-testid="gold-singles-hero-title">
            ALL GOLD CRUMPLE SINGLES
          </h1>
          <p className="text-white/80 mt-4 max-w-2xl text-sm md:text-base">
            Search, sort and browse the complete CollectorUp Gold Crumple catalog.
            <span className="text-white/50">
              {" "}Total: <span data-testid="gold-singles-total-catalog">{anyLoading ? "…" : totalCatalog}</span> products.
            </span>
          </p>
        </div>
      </section>

      {/* Controls — compact on mobile:
          Row 1: search (full width, small)
          Row 2 (md:hidden collapsible): tap "Filters" chip to reveal
          Sort + Type. Desktop shows all three inline. */}
      <section className="border-b border-neutral-200 bg-neutral-50 py-2.5 md:py-6 sticky top-[64px] z-20"
               data-testid="gold-singles-controls">
        <div className="container-gm">
          {/* Mobile compact row */}
          <div className="md:hidden flex items-center gap-2">
            <div className="relative flex-1 min-w-0">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--smoke)]" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="ARG17, Messi, Brazil…"
                className="w-full pl-8 pr-2 py-1.5 rounded-lg border-2 border-[var(--ink)]/25 focus:border-[var(--ink)] focus:outline-none text-sm bg-white"
                data-testid="gold-singles-search"
                spellCheck={false}
              />
            </div>
            <button
              type="button"
              onClick={() => setFiltersOpen((o) => !o)}
              className={`shrink-0 border-2 border-[var(--ink)] rounded-lg px-2 py-1.5 text-[11px] font-mono uppercase tracking-wide ${filtersOpen ? "bg-[var(--panini-yellow)]" : "bg-white"}`}
              aria-expanded={filtersOpen ? "true" : "false"}
              data-testid="gold-singles-filters-toggle"
            >
              Filters {(sort !== "price-desc" || type !== "all") ? "•" : ""}
            </button>
          </div>
          {filtersOpen && (
            <div className="md:hidden grid grid-cols-2 gap-2 mt-2" data-testid="gold-singles-filters-panel">
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                className="w-full py-1.5 px-2 rounded-lg border-2 border-[var(--ink)]/25 focus:border-[var(--ink)] focus:outline-none text-xs bg-white"
                data-testid="gold-singles-sort"
              >
                {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <select
                value={type}
                onChange={(e) => setType(e.target.value)}
                className="w-full py-1.5 px-2 rounded-lg border-2 border-[var(--ink)]/25 focus:border-[var(--ink)] focus:outline-none text-xs bg-white"
                data-testid="gold-singles-filter-type"
              >
                {TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          )}

          {/* Desktop full controls */}
          <div className="hidden md:flex flex-col md:flex-row gap-3 md:gap-4 md:items-end">
            <label className="flex-1 min-w-0">
              <div className="text-[10px] font-mono uppercase text-[var(--smoke)] mb-1">Search Gold Stickers</div>
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--smoke)]" />
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="ARG17, Messi, Ronaldo, FWC12, Brazil…"
                  className="w-full pl-9 pr-3 py-2 rounded-xl border-2 border-[var(--ink)]/25 focus:border-[var(--ink)] focus:outline-none text-sm md:text-base bg-white"
                  data-testid="gold-singles-search-desktop"
                  spellCheck={false}
                />
              </div>
            </label>
            <label className="md:w-56">
              <div className="text-[10px] font-mono uppercase text-[var(--smoke)] mb-1">Sort</div>
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                className="w-full py-2 px-3 rounded-xl border-2 border-[var(--ink)]/25 focus:border-[var(--ink)] focus:outline-none text-sm bg-white"
                data-testid="gold-singles-sort-desktop"
              >
                {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </label>
            <label className="md:w-44">
              <div className="text-[10px] font-mono uppercase text-[var(--smoke)] mb-1">Sticker Type</div>
              <select
                value={type}
                onChange={(e) => setType(e.target.value)}
                className="w-full py-2 px-3 rounded-xl border-2 border-[var(--ink)]/25 focus:border-[var(--ink)] focus:outline-none text-sm bg-white"
                data-testid="gold-singles-filter-type-desktop"
              >
                {TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </label>
          </div>
          <div className="hidden md:block mt-2 text-[11px] text-[var(--smoke)]" data-testid="gold-singles-result-summary">
            {anyLoading
              ? "Loading full Gold catalog from Shopify…"
              : searching
                ? `${totalMatches} match${totalMatches === 1 ? "" : "es"} for "${query.trim()}" across all ${totalCatalog} Gold products — empty sections auto-hide.`
                : `Showing all ${totalCatalog} Gold products across 3 buckets. Lower-tier buckets paginate ${PAGE_SIZE} rows at a time.`}
          </div>
        </div>
      </section>

      {/* PREMIUM $20+ — visual cards */}
      {(!searching || viewPremium.length > 0) && (
      <section id="gold-singles-premium" className="py-8 md:py-12 border-b border-neutral-200 scroll-mt-32"
               data-testid="gold-singles-bucket-premium">
        <div className="container-gm">
          <div className="flex items-baseline justify-between gap-3 flex-wrap mb-6">
            <h2 className="h-display text-2xl md:text-3xl">PREMIUM GOLD SINGLES — $20+</h2>
            <span className="text-xs font-mono text-[var(--smoke)]"
                  data-testid="gold-singles-bucket-count-premium">
              {loading.premium ? "Loading…" : `${viewPremium.length}${searching ? ` / ${premium.length}` : ""} products`}
            </span>
          </div>
          {loading.premium ? (
            <SkeletonCards />
          ) : errors.premium ? (
            <BucketError msg={errors.premium} />
          ) : viewPremium.length === 0 ? (
            <BucketEmpty query={query} />
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5"
                 data-testid="gold-singles-grid-premium">
              {viewPremium.map((p) => (
                <GoldSingleCard key={p.shopify_product_id} product={p} />
              ))}
            </div>
          )}
        </div>
      </section>
      )}

      {/* $10–$19 — compact list rows */}
      {(!searching || viewMidtier.length > 0) && (
      <section id="gold-singles-midtier" className="py-8 md:py-12 border-b border-neutral-200 scroll-mt-32"
               data-testid="gold-singles-bucket-midtier">
        <div className="container-gm">
          <div className="flex items-baseline justify-between gap-3 flex-wrap mb-4">
            <h2 className="h-display text-2xl md:text-3xl">GOLD SINGLES — $10–$19</h2>
            <span className="text-xs font-mono text-[var(--smoke)]"
                  data-testid="gold-singles-bucket-count-midtier">
              {loading.midtier
                ? "Loading…"
                : searching
                  ? `${viewMidtier.length}${searching ? ` / ${midtier.length}` : ""} products`
                  : `Showing ${Math.min(visibleMid, viewMidtier.length)} of ${viewMidtier.length} products`}
            </span>
          </div>
          {loading.midtier ? (
            <div className="text-sm text-[var(--smoke)]">Loading $10–$19 rows…</div>
          ) : errors.midtier ? (
            <BucketError msg={errors.midtier} />
          ) : viewMidtier.length === 0 ? (
            <BucketEmpty query={query} />
          ) : (
            <>
              <div className="border-t border-neutral-200"
                   data-testid="gold-singles-list-midtier">
                {midRendered.map((p) => (
                  <GoldSingleRow key={p.shopify_product_id} product={p} />
                ))}
              </div>
              {!searching && visibleMid < viewMidtier.length && (
                <div className="mt-6 text-center">
                  <button
                    type="button"
                    onClick={() => setVisibleMid(v => v + PAGE_SIZE)}
                    className="btn-yellow"
                    data-testid="gold-singles-load-more-midtier"
                  >
                    LOAD MORE ({viewMidtier.length - visibleMid} remaining)
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>
      )}

      {/* $9 & Under — compact list rows, paginated */}
      {(!searching || viewUnderten.length > 0) && (
      <section id="gold-singles-underten" className="py-8 md:py-12 border-b border-neutral-200 scroll-mt-32"
               data-testid="gold-singles-bucket-underten">
        <div className="container-gm">
          <div className="flex items-baseline justify-between gap-3 flex-wrap mb-4">
            <h2 className="h-display text-2xl md:text-3xl">GOLD SINGLES — $9 &amp; UNDER</h2>
            <span className="text-xs font-mono text-[var(--smoke)]"
                  data-testid="gold-singles-bucket-count-underten">
              {loading.underten
                ? "Loading…"
                : searching
                  ? `${viewUnderten.length} / ${underten.length} products`
                  : `Showing ${Math.min(visibleUnder, viewUnderten.length)} of ${viewUnderten.length} products`}
            </span>
          </div>
          {loading.underten ? (
            <div className="text-sm text-[var(--smoke)]">Loading $9 &amp; Under rows…</div>
          ) : errors.underten ? (
            <BucketError msg={errors.underten} />
          ) : viewUnderten.length === 0 ? (
            <BucketEmpty query={query} />
          ) : (
            <>
              <div className="border-t border-neutral-200"
                   data-testid="gold-singles-list-underten">
                {underRendered.map((p) => (
                  <GoldSingleRow key={p.shopify_product_id} product={p} />
                ))}
              </div>
              {!searching && visibleUnder < viewUnderten.length && (
                <div className="mt-6 text-center">
                  <button
                    type="button"
                    onClick={() => setVisibleUnder(v => v + PAGE_SIZE)}
                    className="btn-yellow"
                    data-testid="gold-singles-load-more-underten"
                  >
                    LOAD MORE ({viewUnderten.length - visibleUnder} remaining)
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>
      )}

      {searching && totalMatches === 0 && !anyLoading ? (
        <div className="container-gm py-12 text-center text-sm text-[var(--smoke)]"
             data-testid="gold-singles-no-results">
          No Gold stickers found for "{query.trim()}".
        </div>
      ) : null}

      <div className="container-gm py-8 text-center">
        <Link to="/shop"
              className="text-sm underline underline-offset-2 text-[var(--smoke)] hover:text-[var(--ink)]"
              data-testid="gold-singles-back-to-shop">
          ← Back to Shop
        </Link>
      </div>

      {/* Hidden Shopify Buy Button — pre-warms the Shopify Buy SDK cart
          so Add to Cart clicks on any card OR row land in the SAME
          shared cart drawer used across CollectorUp. */}
      <div className="sr-only" aria-hidden data-testid="shopify-buy-button-hidden">
        <ShopifyBuyButton />
      </div>
    </div>
  );
}

function SkeletonCards() {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
      {[0,1,2,3].map(i => (
        <div key={i} className="rounded-2xl border-2 border-neutral-200 bg-neutral-100 aspect-[3/4] animate-pulse" />
      ))}
    </div>
  );
}

function BucketError({ msg }) {
  return (
    <div className="rounded-2xl border-2 border-red-200 bg-red-50 p-6 text-sm text-red-800">
      Could not load Gold Singles bucket: {msg}
    </div>
  );
}

function BucketEmpty({ query }) {
  return (
    <div className="rounded-2xl border-2 border-dashed border-neutral-300 p-8 text-center text-sm text-[var(--smoke)]">
      {query ? `No matches for "${query.trim()}" in this bucket.` : "No products in this bucket right now."}
    </div>
  );
}
